import {
  IconButton,
  type IconName,
  HighlightIcon,
  Tag,
  type HighlightIconVariant,
} from '@dev-spendesk/grapes';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import classNames from 'classnames';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { useFeature } from 'src/core/common/hooks/useFeature';
import {
  useTranslation,
  type TGlobalFunctionTyped,
} from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { useCustomFieldsQuery } from 'src/core/modules/budgets/apis';

import { type Dimension } from '../../approvalWorkflow';
import {
  getSiblingNodesCenterPosition,
  NEW_NODE_OFFSET,
} from '../../helpers/layoutNavigation';
import { type ApprovalSchemeWithMemberNames } from '../../hooks/useApprovalSchemesQuery';
import { useGetDimensions } from '../../hooks/useGetDimensions';
import { useRegisterNodeHeight } from '../../hooks/useRegisterNodeHeight';
import { useZoomTo, ZOOM_OFFSET } from '../../hooks/useZoomTo';
import { useWorkflowContext } from '../../state/WorkflowContext';
import { ApprovalSchemePreview } from '../ApprovalSchemePreview/ApprovalSchemePreview';

export type BaseNodeData = {
  schemeId?: string;
  dimension?: Dimension;
  values?: { id: string; name: string }[] | null;
  approvalScheme?: ApprovalSchemeWithMemberNames;
};

type Props = NodeProps & {
  data: BaseNodeData;
};

const handleStyle = {
  background: 'transparent',
  border: 'none',
  width: 0,
  height: 0,
};

export const BaseNode = ({ id, data }: Props) => {
  const {
    createAndConnectNode,
    setSelectedNodeId,
    selectedNodeId,
    getAllParentNodes,
    getChildrenNodes,
    getSiblingNodes,
    nodes: displayNodes,
    newNodeId,
    workflowView,
  } = useWorkflowContext();
  const isTemporaryApprovalWorkflowSchemePreviewEnabled = useFeature(
    FEATURES.TMP_APPROVAL_WORKFLOW_SCHEME_PREVIEW,
  );
  const nodeReference = useRegisterNodeHeight<HTMLDivElement>(id);
  const isSelected = selectedNodeId === id;
  const { t } = useTranslation('global');
  const dimensions = useGetDimensions();
  const zoomTo = useZoomTo();
  const customFieldsQueryState = useCustomFieldsQuery();
  const customFields = unwrapQuery(customFieldsQueryState)?.customFields;
  const dimensionCustomFieldId =
    data.dimension?.type === 'customField' && data.dimension.customFieldId;
  const dimensionCustomFieldName = dimensionCustomFieldId
    ? customFields?.find((cf) => cf.id === dimensionCustomFieldId)?.name
    : undefined;
  // Check if all dimensions are selected
  const parentNodes = getAllParentNodes(id);
  const parentDimensions = parentNodes.map(
    (node) => node.type === 'baseNode' && node.data.dimension?.type,
  );
  const parentDimensionsAndCurrentDimension = [
    ...parentDimensions,
    data.dimension?.type,
  ];
  const hasAllDimensionsSelected =
    parentDimensionsAndCurrentDimension.length === dimensions.length;
  const childrenNodes = getChildrenNodes(id);
  const hasChildrenNodes = childrenNodes.length > 0;

  const currentNode = displayNodes.find((node) => node.id === id);
  const currentNodeHasScheme =
    currentNode?.type === 'baseNode' &&
    currentNode?.data.schemeId !== undefined;
  const isPlaceholderNode =
    data.values === undefined && data.dimension === undefined;
  const shouldShowNoSchemeWarning = !currentNodeHasScheme && !isPlaceholderNode;
  const isCreationLocked = Boolean(newNodeId);

  return (
    <div ref={nodeReference} className="flex items-center">
      <button
        type="button"
        onClick={() => {
          const selectedNode = displayNodes.find((node) => node.id === id);
          if (selectedNode) {
            zoomTo({
              x: selectedNode.position.x + ZOOM_OFFSET,
              y: selectedNode.position.y,
            });
          }
          setSelectedNodeId(id, true);
        }}
        className={classNames(
          'rounded-12 elevation-s flex w-[320px] flex-col gap-8 border border-solid p-16',
          isSelected
            ? 'border-border-default bg-background-primary-default outline outline-offset-[var(--grapes-focus-ring-offset)] outline-[var(--grapes-color-background-primary-brand-default)]'
            : 'border-border-default bg-background-primary-default hover:bg-background-primary-hover cursor-pointer',
        )}
      >
        <div className="flex items-center justify-between">
          {isPlaceholderNode ? (
            <div className="flex h-[60px] items-center">
              <button
                type="button"
                className={classNames(
                  'bg-background-primary-default text-content-disabled title-m h-full w-[240px] cursor-pointer border-none text-left',
                )}
                onClick={() => setSelectedNodeId(id, true)}
              >
                {t('approvalWorkflows.dimensions.selectValue')}
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-8">
                {data.dimension && (
                  <HighlightIcon
                    variant={getVariant(data.dimension.type)}
                    name={getDimensionIconName(data.dimension)}
                    size={24}
                  />
                )}
                <div className="text-content-primary body-s">
                  {data.dimension &&
                    getDimensionHeader(
                      data.dimension,
                      t,
                      dimensionCustomFieldName,
                    )}
                </div>
              </div>
              <div className="text-content-primary title-m mt-4 max-w-[285px] text-left">
                {data.values === null && (
                  <div className="text-content-brand-default">
                    {data.dimension && getOthersText(data.dimension.type, t)}
                  </div>
                )}
                {data.dimension && data.values && (
                  <div className="line-clamp-2">
                    {data.values === null ? (
                      <>{t('approvalWorkflows.others')}</>
                    ) : (
                      data.values.map((value) => value.name).join(', ')
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {shouldShowNoSchemeWarning && (
          <Tag
            variant="warning"
            className="self-start"
            iconName="triangle-warning"
          >
            {t('approvalWorkflows.dimensions.noSchemeWarning')}
          </Tag>
        )}
        {isTemporaryApprovalWorkflowSchemePreviewEnabled &&
          workflowView === 'extended' &&
          data.approvalScheme && (
            <>
              <div className="separator my-16" />
              <ApprovalSchemePreview approvalScheme={data.approvalScheme} />
            </>
          )}
      </button>
      {!hasAllDimensionsSelected && !hasChildrenNodes && (
        <IconButton
          variant="tertiaryNeutral"
          disabled={isCreationLocked}
          onClick={() => {
            const siblingNodes = getSiblingNodes(id);
            const siblingNodesCenterPosition =
              getSiblingNodesCenterPosition(siblingNodes);
            zoomTo({
              x: siblingNodesCenterPosition.x + NEW_NODE_OFFSET,
              y: siblingNodesCenterPosition.y,
            });
            createAndConnectNode({
              sourceNodeId: id,
            });
          }}
          iconName="circle-plus"
          aria-label="plus"
        />
      )}
      <Handle
        style={handleStyle}
        type="target"
        id="leftHandle"
        position={Position.Left}
      />
      <Handle
        style={handleStyle}
        type="source"
        id="rightHandle"
        position={Position.Right}
      />
    </div>
  );
};

const getOthersText = (
  dimensionName: Dimension['type'],
  t: TGlobalFunctionTyped,
) => {
  switch (dimensionName) {
    case 'costCenter':
      return t('approvalWorkflows.dimensions.costCenter.others');
    case 'team':
      return t('approvalWorkflows.dimensions.team.others');
    case 'spendType':
      return t('approvalWorkflows.dimensions.spendType.others');
    case 'customField':
      return t('approvalWorkflows.dimensions.customField.others');
    case 'expenseCategory':
      return t('approvalWorkflows.dimensions.expenseCategory.others');
    default:
      return '';
  }
};

const getVariant = (dimensionName: Dimension['type']): HighlightIconVariant => {
  switch (dimensionName) {
    case 'team':
      return 'lemon';
    case 'costCenter':
      return 'peach';
    case 'spendType':
      return 'purple';
    case 'customField':
      return 'lemon';
    case 'expenseCategory':
      return 'ocean';
    default:
      return 'purple';
  }
};

const getDimensionHeader = (
  dimension: Dimension,
  t: TGlobalFunctionTyped,
  customFieldName?: string,
) => {
  switch (dimension.type) {
    case 'costCenter':
      return t('approvalWorkflows.dimensions.costCenter.nodePreviewTitle');
    case 'team':
      return t('approvalWorkflows.dimensions.team.nodePreviewTitle');
    case 'spendType':
      return t('approvalWorkflows.dimensions.spendType.nodePreviewTitle');
    case 'customField':
      return t(
        'approvalWorkflows.dimensions.customField.nodePreviewTitleWithCustomFieldName',
        {
          customFieldName,
        },
      );
    case 'expenseCategory':
      return t('approvalWorkflows.dimensions.expenseCategory.nodePreviewTitle');
    default:
      return '';
  }
};

const getDimensionIconName = (dimension: Dimension): IconName => {
  switch (dimension.type) {
    case 'costCenter':
      return 'building-bank';
    case 'team':
      return 'person-group';
    case 'spendType':
      return 'dollar-circle-arrow';
    case 'customField':
      return 'gear';
    case 'expenseCategory':
      return 'banknote';
    default:
      return 'dollar-circle-arrow';
  }
};
